import React from 'react';
import { IDENTITY_A, identityAssetUrl } from '../../../brandIdentity.js';
import { newNotebook, layoutNotebook } from '../../../notebook.js';
import { notebookFonts } from './notebookClient.js';

// One page of the «Cuaderno de trabajo» (or of an Instagram or LinkedIn piece of identity A), drawn as SVG from the
// layout items: the same geometry the PDF and the PNG draw, so what Ale reviews here is what she downloads. Kerning and Caveat's alternates are off,
// as in the PDF.

const FAMILY = Object.fromEntries(Object.entries(IDENTITY_A.fonts).map(([k, f]) => [k, `'${f.family}', ${k.startsWith('body') ? 'Arial, sans-serif' : 'cursive'}`]));
const NOTE_FEATURES = '"calt" 0, "liga" 0, "clig" 0, "dlig" 0';

export function NotebookPage({ page, size, images = {}, maxWidth = 420, label, shadow = true, guides = false, crop = null }) {
  // guides: the zones of the platform's interface and the profile crop, drawn over the preview only (never in the files).
  // crop: show only that part (how the profile grid shows it).
  const vb = crop ? `${crop.x} ${crop.y} ${crop.w} ${crop.h}` : `0 0 ${size.w} ${size.h}`;
  const uid = React.useId().replace(/[^a-zA-Z0-9]/g, '');
  return (
    <svg viewBox={vb} role="img" aria-label={label || `Página ${page.n}`}
      style={{ display: 'block', width: '100%', maxWidth, height: 'auto', aspectRatio: crop ? `${crop.w} / ${crop.h}` : `${size.w} / ${size.h}`, boxShadow: shadow ? '0 1px 6px rgba(0,0,0,.18)' : 'none', background: '#fff' }}>
      {page.items.map((it, i) => {
        if (it.t === 'rect') return <rect key={i} x={it.x} y={it.y} width={it.w} height={it.h} fill={it.fill} />;
        if (it.t === 'texture') return <image key={i} href={identityAssetUrl(IDENTITY_A.texture)} x={it.x ?? 0} y={it.y ?? 0} width={it.w ?? size.w} height={it.h ?? size.h} preserveAspectRatio="none" />;
        if (it.t === 'slot') return <rect key={i} x={it.x} y={it.y} width={it.w} height={it.h} fill="none" stroke={it.dark ? 'rgba(244,241,234,.5)' : 'rgba(35,48,47,.4)'} strokeWidth={4} strokeDasharray="18 12" />;
        if (it.t === 'path') return <path key={i} d={it.d} fill={it.fill || 'none'} stroke={it.stroke || 'none'} strokeWidth={it.width || 0} strokeOpacity={it.stroke ? it.opacity ?? 1 : undefined} fillOpacity={it.fill ? it.opacity ?? 1 : undefined} strokeLinecap="round" strokeLinejoin="round" />;
        if (it.t === 'text') return <text key={i} x={it.x} y={it.y} fontFamily={FAMILY[it.font]} fontSize={it.size} fill={it.color} style={{ whiteSpace: 'pre', fontKerning: 'none', fontFeatureSettings: it.font === 'note' ? NOTE_FEATURES : undefined }}>{it.text}</text>;
        if (it.t === 'image') {
          const src = images[it.photoId]?.src;
          if (!src) return <rect key={i} x={it.clip.x} y={it.clip.y} width={it.clip.w} height={it.clip.h} fill="none" stroke="rgba(244,241,234,.4)" strokeDasharray="6 5" />;
          return (
            <g key={i}>
              <clipPath id={`c${uid}${i}`}><rect x={it.clip.x} y={it.clip.y} width={it.clip.w} height={it.clip.h} /></clipPath>
              <image href={src} x={it.x} y={it.y} width={it.w} height={it.h} preserveAspectRatio="none" clipPath={`url(#c${uid}${i})`} />
            </g>
          );
        }
        return null;
      })}
      {guides && (page.guides || []).map((g, k) => (g.kind === 'feed' ? (
        // an orientative guide: what some previews may crop (outside the area), only on screen
        <g key={`g${k}`} aria-hidden="true">
          <path d={`M0 0H${size.w}V${size.h}H0Z M${g.x} ${g.y}V${g.y + g.h}H${g.x + g.w}V${g.y}Z`} fill="rgba(217,58,43,.45)" fillRule="evenodd" />
          <rect x={g.x + 3} y={g.y + 3} width={g.w - 6} height={g.h - 6} fill="none" stroke="#D93A2B" strokeWidth={4} strokeDasharray="22 14" />
          <rect x={g.x + 16} y={g.y + 14} width={Math.min(g.w - 32, 34 * 0.56 * String(g.label).length + 28)} height={50} rx={6} fill="rgba(217,58,43,.9)" />
          <text x={g.x + 30} y={g.y + 50} fontFamily="Arial, sans-serif" fontWeight="700" fontSize={34} fill="#fff">{g.label}</text>
        </g>
      ) : g.kind === 'crop' ? (
        <g key={`g${k}`} aria-hidden="true">
          <path d={`M0 0H${size.w}V${size.h}H0Z M${g.x} ${g.y}V${g.y + g.h}H${g.x + g.w}V${g.y}Z`} fill="rgba(0,0,0,.42)" fillRule="evenodd" />
          <rect x={g.x + 3} y={g.y + 3} width={g.w - 6} height={g.h - 6} fill="none" stroke="#FFD84D" strokeWidth={6} strokeDasharray="26 16" />
          <text x={g.x + 24} y={Math.max(48, g.y - 22)} fontFamily="Arial, sans-serif" fontWeight="700" fontSize={40} fill="#FFD84D">{g.label}</text>
        </g>
      ) : (
        <g key={`g${k}`} aria-hidden="true">
          <rect x={g.x} y={g.y} width={g.w} height={g.h} fill="rgba(217,58,43,.28)" stroke="#D93A2B" strokeWidth={4} strokeDasharray="14 10" />
          <text x={g.x + 14} y={g.y + g.h / 2 + 12} fontFamily="Arial, sans-serif" fontWeight="700" fontSize={Math.min(34, g.h / 2.6)} fill="#fff">{g.label}</text>
        </g>
      )))}
      {page.imageZone && !page.items.some(i => i.t === 'image') && (
        <rect x={page.imageZone.x + 6} y={page.imageZone.y + 6} width={page.imageZone.w - 12} height={page.imageZone.h - 12} fill="none" stroke="rgba(244,241,234,.35)" strokeDasharray="6 5" />
      )}
    </svg>
  );
}

/** The template's sample pages, for the catalog (sample texts; no photo). */
export function NotebookSample({ maxWidth = 150, pages = 4 }) {
  const [state, setState] = React.useState(null);
  React.useEffect(() => {
    let on = true;
    notebookFonts().then(({ measure }) => on && setState({ layout: layoutNotebook(newNotebook(), { measure }) })).catch(e => on && setState({ error: e.message }));
    return () => { on = false; };
  }, []);
  if (!state) return <div style={{ fontSize: 12, color: 'var(--globant-muted)' }}>Cargando el ejemplo…</div>;
  if (state.error) return <div role="alert" style={{ fontSize: 12, color: '#B4362C' }}>{state.error}</div>;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {state.layout.pages.slice(0, pages).map(p => <div key={p.n} style={{ flex: `0 1 ${maxWidth}px`, minWidth: 0 }}><NotebookPage page={p} size={state.layout.size} maxWidth={maxWidth} label={`Ejemplo del cuaderno, página ${p.n}`} /></div>)}
    </div>
  );
}
